import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box, Typography } from "@material-ui/core";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import {
  updateRepresentative,
  updateRepresentativeFilters,
} from "redux/reducers/criarVitrineReducer";

import { DEFAULT_REPRESENTATIVE_FILTERS } from "../utils/wizard-helpers";
import { buildRepresentativePagedQuery } from "../../shared/utils/representative-api-params";
import { REPRESENTATIVE_PAGE_SIZE } from "../../shared/utils/representative-helpers";

import RepresentativeFilters from "../../shared/components/representative-filters";
import RepresentativeSelectionList from "../../shared/components/representative-selection-list";

const REPRESENTATIVE_STATUS_OPTIONS = [
  { id: "TODOS", value: "TODOS", label: "Todos" },
  { id: "ATIVO", value: "ATIVO", label: "Ativo" },
  { id: "INATIVO", value: "INATIVO", label: "Inativo" },
];

export default function TargetAudience() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const dispatch = useDispatch();
  const snackbar = useSnackbar();

  const {
    formData,
    representatives = [],
    representativeFilters,
  } = useSelector((state) => state.criarVitrine);

  const filters = useMemo(
    () => ({
      ...DEFAULT_REPRESENTATIVE_FILTERS,
      ...representativeFilters,
    }),
    [representativeFilters],
  );

  const idMarca = formData?.brand?.value;

  const selectedIds = useMemo(
    () => new Set(representatives.map((r) => r.Id)),
    [representatives],
  );

  const fetchSeqRef = useRef(0);

  const fetchPage = useCallback(
    async (pageToLoad, filtersToUse) => {
      const seq = ++fetchSeqRef.current;

      if (!idMarca) {
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        return;
      }

      setIsLoading(true);
      try {
        const query = buildRepresentativePagedQuery(filtersToUse, {
          idMarca,
          page: pageToLoad,
          pageSize: REPRESENTATIVE_PAGE_SIZE,
        });

        const response = await Api.get(
          "list-b2b-showcase-representatives",
          "showcaseB2B",
          `?${query}`,
          true,
        );

        // Ignora respostas de buscas antigas que resolveram depois de uma mais recente
        // (ex.: digitação rápida no CNPJ ou troca de página antes da resposta anterior voltar).
        if (seq !== fetchSeqRef.current) return;

        const result = response?.Result;
        const list = (result?.Lista ?? result?.Result ?? []).map((rep) => ({
          ...rep,
          __selectionId: rep.Id,
        }));

        setItems(list);
        setTotalItems(result?.TotalRegistros ?? 0);
        setTotalPages(result?.TotalDePaginas ?? 0);
        setPage(result?.Pagina ?? pageToLoad);
      } catch (error) {
        if (seq !== fetchSeqRef.current) return;
        console.error(error);
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        snackbar.showMessage(
          "Não foi possível carregar os representantes. Tente novamente.",
          { severity: "error" },
        );
      } finally {
        if (seq === fetchSeqRef.current) setIsLoading(false);
      }
    },
    [idMarca, snackbar],
  );

  const handleFiltersChange = useCallback(
    (updates) => {
      dispatch(updateRepresentativeFilters(updates));
      setPage(0);
    },
    [dispatch],
  );

  useEffect(() => {
    fetchPage(page, filters);
  }, [fetchPage, page, filters]);

  const handleToggleItem = useCallback(
    (item) => {
      const id = item.__selectionId;
      if (selectedIds.has(id)) {
        dispatch(
          updateRepresentative(representatives.filter((r) => r.Id !== id)),
        );
      } else {
        const { __selectionId, ...rep } = item;
        dispatch(updateRepresentative([...representatives, rep]));
      }
    },
    [dispatch, representatives, selectedIds],
  );

  const selectVisible = useCallback(
    (visibleIds) => {
      const visibleIdSet = new Set(visibleIds);
      const map = new Map(representatives.map((r) => [r.Id, r]));
      items
        .filter((item) => visibleIdSet.has(item.__selectionId))
        .forEach((item) => {
          const { __selectionId, ...rep } = item;
          map.set(__selectionId, rep);
        });
      dispatch(updateRepresentative(Array.from(map.values())));
    },
    [dispatch, items, representatives],
  );

  const deselectVisible = useCallback(
    (visibleIds) => {
      const hide = new Set(visibleIds);
      dispatch(
        updateRepresentative(representatives.filter((r) => !hide.has(r.Id))),
      );
    },
    [dispatch, representatives],
  );

  const handleSelectAllResults = useCallback(async () => {
    if (!idMarca) return;

    const bulkPageSize = 100;
    const all = [];
    let total = 0;
    let pageNum = 0;

    // Sem teto de seleção: pagina até trazer tudo. pageNum > 200 abaixo é só uma trava de
    // segurança contra loop infinito (200 páginas × 100 = 20000), não um limite de negócio.
    for (;;) {
      const query = buildRepresentativePagedQuery(filters, {
        idMarca,
        page: pageNum,
        pageSize: bulkPageSize,
      });

      const response = await Api.get(
        "list-b2b-showcase-representatives",
        "showcaseB2B",
        `?${query}`,
        true,
      );

      const result = response?.Result;
      const list = (result?.Lista ?? result?.Result ?? []).map((rep) => ({
        ...rep,
        __selectionId: rep.Id,
      }));

      total = result?.TotalRegistros ?? total;
      all.push(...list);

      if (list.length === 0 || all.length >= total) break;
      pageNum += 1;
      if (pageNum > 200) break;
    }

    if (all.length < total) {
      snackbar.showMessage(
        `Não foi possível carregar todos os ${total} representantes encontrados. Refine os filtros.`,
        { severity: "warning" },
      );
    }

    const map = new Map(representatives.map((r) => [r.Id, r]));
    all.forEach((item) => {
      const { __selectionId, ...rep } = item;
      map.set(__selectionId, rep);
    });
    dispatch(updateRepresentative(Array.from(map.values())));
  }, [dispatch, filters, idMarca, representatives, snackbar]);

  const handleClearAllSelected = useCallback(() => {
    dispatch(updateRepresentative([]));
  }, [dispatch]);

  return (
    <Box>
      <RepresentativeFilters
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        showGrupoRazaoFilters={false}
        statusOptions={REPRESENTATIVE_STATUS_OPTIONS}
      />

      <RepresentativeSelectionList
        isLoading={isLoading}
        items={items}
        totalItems={totalItems}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        selectedIds={selectedIds}
        onToggleItem={handleToggleItem}
        onSelectVisible={selectVisible}
        onDeselectVisible={deselectVisible}
        onSelectAllResults={handleSelectAllResults}
        onDeselectAll={handleClearAllSelected}
        selectAllDialogTitle="Selecionar representantes"
        selectAllEntityLabel="representantes"
        emptyMessage="Nenhum representante encontrado com os filtros aplicados."
        selectionFooter={
          representatives.length > 0 ? (
            <Typography
              variant="caption"
              style={{
                display: "block",
                marginTop: 8,
                color: themeColorsDesingSystem?.gray?.dark,
              }}
            >
              {representatives.length} representante(s) selecionado(s)
            </Typography>
          ) : null
        }
      />
    </Box>
  );
}
